import {useMutation} from "@apollo/client";
import {useCallback, useEffect, useRef, useState} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {useLocation, useNavigate} from "react-router";

import {PageForm} from "@/components/PageForm/PageForm.tsx";
import {useSubmitWrapper} from "@/lib/hooks/useSubmitWrapper.ts";
import {CreateStatusPageResult, GQL_CREATE_STATUS_PAGE} from "@/lib/queries/statuspages.ts";
import {QuerySuccess, ResponseStatus, ServerErrors} from "@/lib/types/common.ts";
import {StatusPage} from "@/lib/types/statuspages.ts";
import {LimitCheckStatuspages} from "@/pages/billing/v2/InstantCheckout/LimitCheckStatuspages.tsx";
import {LimitCheckerComponent} from "@/pages/billing/v2/InstantCheckout/LimitCheckWithInstantCheckout.tsx";
import {statusPageFormDataProcessing} from "@/pages/statuspages/shared/StatusPageForm.utils.ts";
import {CreateStatusPageFormData} from "@/pages/statuspages/statuspages-create/CreateStatusPageForm.types.ts";
import {
  CreateStatusPageFormDefaultValues,
  NewStatusPageYupResolver,
} from "@/pages/statuspages/statuspages-create/CreateStatusPageForm.utils.tsx";
import {StatusPageGeneralTabV2} from "@/pages/statuspages/tabs/general/StatusPageGeneralTabV2.tsx";
import {setServerErrors} from "@/pages/vault/vaultForm/CredentialFormUtils.ts";

export const CreateStatusPageForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const statuspagesLimitCheckRef = useRef<LimitCheckerComponent>();

  const [createNewStatusPage, {data}] = useMutation<CreateStatusPageResult>(GQL_CREATE_STATUS_PAGE);

  const methods = useForm<CreateStatusPageFormData>({
    defaultValues: CreateStatusPageFormDefaultValues,
    reValidateMode: "onChange",
    mode: "onChange",
    resolver: NewStatusPageYupResolver,
  });

  const goBack = useCallback(() => {
    const previousPath = location.state?.from?.pathname;
    const defaultPath = "/statuspages/all";
    navigate(previousPath || defaultPath);
  }, [location.state?.from?.pathname, navigate]);

  const newStatusPageErrors =
    data?.response?.status === ResponseStatus.ERROR ? (data?.response?.message as ServerErrors) : undefined;

  const [serverErrorsState, setServerErrorsState] = useState<ServerErrors | undefined>(newStatusPageErrors);

  useEffect(() => {
    if (serverErrorsState) {
      setServerErrors(serverErrorsState, methods.setError, "");
    }
  }, [serverErrorsState, methods.setError]);

  const onSubmit = async (formData: CreateStatusPageFormData) => {
    const result = await createNewStatusPage({
      variables: {
        data: statusPageFormDataProcessing(formData),
      },
    });

    const apiResult = result?.data as CreateStatusPageResult;

    if (apiResult?.response?.status === ResponseStatus.ERROR) {
      setServerErrorsState(apiResult?.response?.message as ServerErrors);
    }

    return apiResult;
  };

  const handleSubmitWithResponse = useSubmitWrapper<CreateStatusPageFormData, CreateStatusPageResult>({
    methods,
    handleSubmit: methods.handleSubmit,
    onSubmit,
    isSuccess: (result) => result?.response?.status === ResponseStatus.SUCCESS && !!result.response.data.id,
    onSuccess: (result) =>
      navigate(`/statuspages/${(result.response as QuerySuccess<StatusPage>).data.id}?tab=Components`),
  });

  return (
    <FormProvider {...methods}>
      <PageForm
        title={"Create Status Page"}
        goBack={goBack}
        serverErrorsState={serverErrorsState}
        formId={"create-status-page-form"}
        submitText={"Create"}
        hasRequiredFields={false}
      >
        <LimitCheckStatuspages ref={statuspagesLimitCheckRef} />
        <form
          id={"create-status-page-form"}
          onSubmit={(e) => {
            e.preventDefault();
            statuspagesLimitCheckRef?.current?.checkIfLimitHit().then((isLimitHit) => {
              if (!isLimitHit) {
                handleSubmitWithResponse(e);
              }
            });
          }}
        >
          <StatusPageGeneralTabV2 />
        </form>
      </PageForm>
    </FormProvider>
  );
};
